
// Shared
import { getTextWidth, formatCurrency } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import { yearCore, yearFilter } from '../taxonomies/year';
import { countryCore, countryFilter } from '../taxonomies/country';
import { trimCore, trimFilter } from './trim';
import { modelCore, modelFilter } from '../taxonomies/model';
import {
  standardFeatureTypeCore,
  standardFeatureTypeFilter
} from '../taxonomies/standardFeatureType';

// Standard Feature core
export function standardFeatureCore() {
  const endpoint = '/standardfeatures';
  const endpointByTrim = '/standardfeatures/StandardFeatureTrim';
  const endpointDescriptionVersion = '/descriptionversion/type/standardfeature/id';
  const endpointPriceVersion = null;
  const detailId = 'standardFeatureTrimId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    endpointByTrim,
    endpointDescriptionVersion,
    endpointPriceVersion,
    detailId,
    getDetailId: getDetailId
  };
}

// Standard Feature listing
export function standardFeatureListing() {

  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = { StandardFeatureDescriptionSearchType: 'C', VehicleDescriptionSearchType: 'C', DerivativeCodeSearchType: 'C' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="data-type"
                    id={params.row.standardFeatureTrimId}
                    endpoint="/standard-feature"
                    deleteApiEndpoint="/standardfeatures"
                    deleteApiPath={`StandardFeatureTrim/${params.row.standardFeatureTrimId}`}
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            {
              field: 'id',
              headerName: 'Row',
              hide: true,
              hideFromColumnsSelector: true
            },
            {
              field: 'realId',
              headerName: 'ID',
              hide: true
            },
            {
              field: 'trimId',
              headerName: 'Trim ID',
              hide: true,
              hideFromColumnsSelector: true
            },
            { field: 'status', headerName: 'Status' },
            { field: 'country', headerName: 'Country' },
            { field: 'year', headerName: 'Year' },
            { field: 'code', headerName: 'Advanced Code' },
            { field: 'sfCode', headerName: 'SF Code' },
            { field: 'type', headerName: 'Type' },
            {
              field: 'price',
              headerName: 'Price',
              valueFormatter: function (params) {
                return formatCurrency(params.value);
              }
            },
            { field: 'description', headerName: 'Description' },
            { field: 'modelCode', headerName: 'Model Code' },
            { field: 'model', headerName: 'Model' },
            { field: 'derivativeCode', headerName: 'Derivative Code' },
            { field: 'derivativeDescription', headerName: 'Derivative Description' },
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          let text = row[id];

          // Add extra chars that might appear on the price
          if (id === 'price') {
            text += '$,.00';
          }

          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: row.rowNumber,
        realId: row.standardFeatureId,
        standardFeatureTrimId: row.standardFeatureTrimId,
        status: row.statusDescription,
        country: row.country,
        code: row.standardFeatureCode,
        sfCode: row.standardFeatureCodeSimple,
        price: row.standardFeaturePrice,
        modelCode: row.modelCode,
        year: row.year,
        derivativeCode: row.derivativeCode,
        trimId: row.trimId,
      };

      // Object values
      const objects = {
        model: row.modelDescription,
        trim: row.trimDescription,
        type: row.standardFeatureType,
        derivativeDescription: row.trimDescription,
        description: row.standardFeatureDescription
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    return [
      [
        {
          size: { xs: 12, md: 6, lg: 4 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 4 },
          component: (
            <Filter
              type="select"
              parameter={yearFilter_.parameter}
              label="Year"
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6, lg: 4 },
          component: (
            <Filter
              type="select"
              parameter={modelFilter_.parameter}
              label="Model"
              endpoint={modelCore_.endpointSimple}
              getRows={modelFilter_.getRows}
            />
          )
        }
      ],
      [
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={[
                'StandardFeatureDescriptionSearchType',
                'StandardFeatureDescription'
              ]}
              label="Description"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['VehicleDescriptionSearchType', 'VehicleDescription']}
              label="Vehicle Description"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['DerivativeCodeSearchType', 'DerivativeCode']}
              label="Derivative Code"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };
}

// Standard Feature detail
export function standardFeatureDetail() {
  const baseUrl = '/standard-feature';

  const watchedParameters = {
    yearId: null,
    trimId: null
  };

  const generateStandardFeatureCode = (
    yearId,
    trimId
  ) => {
    if (
      trimId &&
      trimId.code &&
      yearId &&
      yearId.label
    ) {
      return `${yearId.label}-${trimId.code}-NC***`;
    } else {
      return '';
    }
  };

  const getInputs = (data = null) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      standardFeatureId: '',
      standardFeatureCode: '',
      standardFeatureDescription: '',
      standardFeatureDescriptionText: '',
      standardFeatureTypeId: '',
      standardFeatureEnabled: false,
      countryId: '',
      yearId: '',
      modelId: '',
      trimId: '',
      standardFeaturePrice: 0,
      priceMajorVersion: 0,
      priceMinorVersion: 0
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i] = data[i];
      });

      // Get the text/value from the default culture
      detailObj.standardFeatureDescription.translations.forEach(
        (culture, k) => {
          if (culture.defaultCulture === true) {
            detailObj.standardFeatureDescriptionText = culture.text;
          }
        }
      );
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const yearCore_ = yearCore();
    const yearFilter_ = yearFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const trimCore_ = trimCore();
    const trimFilter_ = trimFilter();

    const standardFeatureTypeCore_ = standardFeatureTypeCore();
    const standardFeatureTypeFilter_ = standardFeatureTypeFilter();

    const standardFeatureCore_ = standardFeatureCore();

    return [
      // Data Version (description.MajorVersion + description.MinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "description",
                versionLabel: "Data Version",
                endPoint: standardFeatureCore_.endpointDescriptionVersion,
                itemId: detailObj.standardFeatureId,
                majorVersion: detailObj.standardFeatureDescription.majorVersion,
                minorVersion: detailObj.standardFeatureDescription.minorVersion,
                dialogueTitle: "Data Version",
                ctaLabel: "Edit Data version"
              }}
            />
          )
        }
      ],
      // Price Version (priceMajorVersion + priceMinorVersion)
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          className: "version-item-wrapper",
          component: (
            <FormInput
              type="version-control"
              inputProps={{
                itemType: "data-type",
                versionType: "price",
                versionLabel: "Price Version",
                endPoint: standardFeatureCore_.endpointPriceVersion,
                itemId: detailObj.standardFeatureId,
                majorVersion: detailObj.priceMajorVersion,
                minorVersion: detailObj.priceMinorVersion,
                dialogueTitle: "Price Version",
                ctaLabel: "Edit Price version"
              }}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryId}
              isCulturesSource={true}
            />
          )
        }
      ],
      // Year
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="year"
              parameter={yearCore_.detailId}
              label="Year"
              required={true}
              endpoint={yearCore_.endpoint}
              getRows={yearFilter_.getRows}
              inputValue={detailObj.yearId}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Model
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              taxonomyName="model"
              parameter={modelCore_.detailId}
              label="Model"
              required={true}
              disabled={true}
              endpoint={modelCore_.endpoint}
              getRows={modelFilter_.getRows}
              inputValue={detailObj.modelId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' }
              ]}
            />
          )
        }
      ],
      // Trim
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select-autocomplete"
              taxonomyName="trim"
              parameter={trimCore_.detailId}
              label="Vehicle"
              required={true}
              disabled={true}
              endpoint={trimCore_.endpoint}
              getRows={trimFilter_.getRows}
              inputValue={detailObj.trimId}
              dependOn={[
                { filter: 'countryid', control: 'countryId' },
                { filter: 'yearid', control: 'yearId' },
                { filter: 'modelid', control: 'modelId' }
              ]}
            />
          )
        }
      ],
      // Type
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={standardFeatureTypeCore_.detailId}
              label="Type"
              required={true}
              endpoint={standardFeatureTypeCore_.endpoint}
              getRows={standardFeatureTypeFilter_.getRows}
              inputValue={detailObj.standardFeatureTypeId}
            />
          )
        }
      ],
      // Description
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="localization"
              parameter="standardFeatureDescription"
              label="Description"
              required={true}
              inputValue={{
                key: detailObj.standardFeatureDescription.key
                  ? detailObj.standardFeatureDescription.key
                  : '',
                translations: detailObj.standardFeatureDescription.translations
                  ? detailObj.standardFeatureDescription.translations
                  : [],
                defaultCultureText: detailObj.standardFeatureDescriptionText
              }}
              dependOn={[{ filter: 'countryid', control: 'countryId' }]}
            />
          )
        }
      ],
      // Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="standardFeatureCode"
              label="Advanced Code"
              required={true}
              inputValue={detailObj.standardFeatureCode}
              generatedValueKey="generateStandardFeatureCode"
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="standardFeatureEnabled"
              required={true}
              inputValue={detailObj.standardFeatureEnabled}
            />
          )
        }
      ],
      // Price
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="price"
              priceType="price"
              label="Price"
              parameter="standardFeaturePrice"
              required={true}
              inputValue={detailObj.standardFeaturePrice}
            />
          )
        }
      ]
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs,
    watchedParameters: watchedParameters,
    generateStandardFeatureCode: generateStandardFeatureCode
  };
}
